// app/(app)/reports/product-requirement.tsx
import { Button } from "@/components/default/forms";
import DatePicker from "@/components/default/forms/DatePicker";
import SearchableSelect from "@/components/default/forms/SearchableSelect";
import { useAppTheme } from "@/hooks/useAppTheme";
import { locationApi } from "@/services/locationApi";
import { ProductRequirementItem, reportsApi } from "@/services/reportsApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchCustomers } from "@/store/slices/customerSlice";
import { fetchEmployees } from "@/store/slices/employeeSlice";
import { showAlert } from "@/utils/alert";
import * as FileSystem from "expo-file-system/legacy";
import * as Sharing from "expo-sharing";
import { useEffect, useState } from "react";
import {
  FlatList,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

export default function ProductRequirementScreen() {
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { employees, loading: employeesLoading } = useAppSelector(
    (state) => state.employees,
  );
  const { customers, loading: customersLoading } = useAppSelector(
    (state) => state.customers,
  );
  const isEmployee = user?.userType === "Employee";

  // Date mode: today or custom range
  const [useToday, setUseToday] = useState(true);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  // Additional filters
  const [selectedAreaId, setSelectedAreaId] = useState<string>("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [customerName, setCustomerName] = useState("");

  // Areas state
  const [areas, setAreas] = useState<{ id: number; name: string }[]>([]);
  const [loadingAreas, setLoadingAreas] = useState(false);

  // Search states
  const [customerSearch, setCustomerSearch] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [products, setProducts] = useState<ProductRequirementItem[]>([]);
  const [reportDateRange, setReportDateRange] = useState<{
    start: string;
    end: string;
  } | null>(null);
  const [employeeName, setEmployeeName] = useState<string | null>(null);
  const [customerNameText, setCustomerNameText] = useState<string | null>(null);

  // Auto‑set employee filter for employees (they can also change it)
  useEffect(() => {
    if (isEmployee && user?.id) {
      setSelectedEmployeeId(user.id.toString());
      setEmployeeName(user.ownerName || "");
    }
  }, [isEmployee, user]);

  // Load areas and employees on mount
  useEffect(() => {
    loadAreas();
    dispatch(fetchEmployees({ page: 1, limit: 100, search: "" }));
  }, []);

  const loadAreas = async () => {
    setLoadingAreas(true);
    try {
      const fetchedAreas = await locationApi.getAreas();
      setAreas(fetchedAreas);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingAreas(false);
    }
  };

  // Fetch customers when area changes
  useEffect(() => {
    if (selectedAreaId) {
      dispatch(
        fetchCustomers({
          page: 1,
          limit: 100,
          search: "",
          areaId: parseInt(selectedAreaId),
        }),
      );
      setSelectedCustomerId("");
      setCustomerName("");
    }
  }, [selectedAreaId]);

  // Debounced customer search (with optional area filter)
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (customerSearch !== undefined) {
        if (selectedAreaId !== "") {
          dispatch(
            fetchCustomers({
              page: 1,
              limit: 20,
              search: customerSearch,
              areaId: Number(selectedAreaId),
            }),
          );
        } else {
          dispatch(
            fetchCustomers({ page: 1, limit: 20, search: customerSearch }),
          );
        }
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [customerSearch, selectedAreaId]);

  // Debounced employee search
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (employeeSearch !== undefined) {
        dispatch(
          fetchEmployees({ page: 1, limit: 20, search: employeeSearch }),
        );
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [employeeSearch]);

  const buildParams = () => {
    const params: any = {};
    if (useToday) {
      params.today = true;
    } else {
      if (!startDate || !endDate) return null;
      params.startDate = startDate;
      params.endDate = endDate;
    }

    if (selectedAreaId) params.areaId = selectedAreaId;
    if (selectedCustomerId) params.customerId = selectedCustomerId;
    if (selectedEmployeeId) params.createdBy = selectedEmployeeId;
    return params;
  };

  const fetchReport = async () => {
    const params = buildParams();
    if (!params) {
      showAlert("Error", "Please select both start and end dates");
      return;
    }
    setLoading(true);
    try {
      const response = await reportsApi.getProductRequirementReport(params);
      setProducts(response.products);
      setReportDateRange({ start: response.startDate, end: response.endDate });
      setEmployeeName(response.employeeName || null);
      setCustomerNameText(response.customerName || null);
    } catch (error: any) {
      showAlert("Error", error.message || "Failed to fetch report");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    const params = buildParams();
    if (!params) {
      showAlert("Error", "Please select both start and end dates");
      return;
    }
    setDownloading(true);
    try {
      const blob = await reportsApi.downloadProductRequirementPdf(params);

      if (Platform.OS === "web") {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else {
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = (reader.result as string).split(",")[1];
          const fileUri =
            FileSystem.cacheDirectory + `product_requirement_${Date.now()}.pdf`;
          await FileSystem.writeAsStringAsync(fileUri, base64, {
            encoding: FileSystem.EncodingType.Base64,
          });
          if (await Sharing.isAvailableAsync()) {
            await Sharing.shareAsync(fileUri);
          } else {
            showAlert("Error", "Sharing not available");
          }
        };
        reader.readAsDataURL(blob);
      }
    } catch (error: any) {
      showAlert("Error", error.message || "Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  const renderProductItem = ({ item }: { item: ProductRequirementItem }) => (
    <View style={styles.productCard}>
      <Text style={styles.productName}>{item.productName}</Text>
      <View style={styles.productRow}>
        <Text style={styles.productLabel}>Total Quantity:</Text>
        <Text style={styles.productValue}>
          {item.totalQuantity} {item.unitSymbol}
        </Text>
      </View>
      <View style={styles.productRow}>
        <Text style={styles.productLabel}>Total Weight:</Text>
        <Text style={styles.productValue}>
          {parseFloat(item.totalWeight).toFixed(2)} {item.unitSymbol}
        </Text>
      </View>
    </View>
  );

  // Compute customer options with selected customer always included
  const getCustomerOptions = () => {
    const baseOptions = customers.map((c) => ({
      label: `${c.fullName} (${c.phone})${c.Area?.name ? ` - ${c.Area.name}` : ""}`,
      value: c.id.toString(),
    }));
    if (
      selectedCustomerId &&
      !baseOptions.some((opt) => opt.value === selectedCustomerId)
    ) {
      const selectedCust = customers.find(
        (c) => c.id.toString() === selectedCustomerId,
      );
      if (selectedCust) {
        baseOptions.unshift({
          label: `${selectedCust.fullName} (${selectedCust.phone})${selectedCust.Area?.name ? ` - ${selectedCust.Area.name}` : ""}`,
          value: selectedCust.id.toString(),
        });
      } else if (customerName) {
        baseOptions.unshift({
          label: customerName,
          value: selectedCustomerId,
        });
      }
    }
    return baseOptions;
  };

  const handleCustomerSelect = (value: string, label: string) => {
    setSelectedCustomerId(value);
    setCustomerName(label);
    setCustomerSearch("");
  };

  const handleClearCustomer = () => {
    setSelectedCustomerId("");
    setCustomerName("");
    setCustomerSearch("");
  };

  const handleEmployeeSelect = (value: string, label: string) => {
    setSelectedEmployeeId(value);
    setEmployeeName(label);
    setEmployeeSearch("");
  };

  const handleClearEmployee = () => {
    setSelectedEmployeeId("");
    setEmployeeName("");
    setEmployeeSearch("");
  };

  const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    keyboardAvoid: { flex: 1 },
    scrollContent: { padding: spacing.lg, paddingBottom: spacing.xl },
    title: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.md,
    },
    switchRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: spacing.md,
    },
    switchLabel: { fontSize: 16, color: colors.text },
    filterRow: {
      flexDirection: "row",
      gap: spacing.sm,
      marginBottom: spacing.md,
    },
    filterButton: {
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: spacing.sm,
      alignItems: "center",
    },
    filterText: { fontSize: 14, color: colors.text },
    inputContainer: { marginBottom: spacing.md },
    buttonRow: {
      flexDirection: "row",
      gap: spacing.sm,
      marginBottom: spacing.lg,
    },
    dateRangeContainer: { marginBottom: spacing.md },
    productCard: {
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: spacing.md,
      marginBottom: spacing.sm,
      borderWidth: 1,
      borderColor: colors.border,
    },
    productName: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.xs,
    },
    productRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginTop: spacing.xs,
    },
    productLabel: { fontSize: 12, color: colors.textLight },
    productValue: { fontSize: 12, fontWeight: "500", color: colors.primary },
    emptyText: {
      textAlign: "center",
      color: colors.textLight,
      marginTop: spacing.xxl,
    },
  });

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardAvoid}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <Text style={styles.title}>Product Requirement</Text>

          <View style={styles.switchRow}>
            <Text style={styles.switchLabel}>Today's requirement</Text>
            <Switch
              value={useToday}
              onValueChange={(val) => {
                setUseToday(val);
                if (val) {
                  setStartDate("");
                  setEndDate("");
                }
              }}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor={colors.background}
            />
          </View>

          {!useToday && (
            <View style={styles.dateRangeContainer}>
              <View style={styles.filterRow}>
                <DatePicker
                  value={startDate}
                  onChange={setStartDate}
                  placeholder="Start Date"
                  containerStyle={{ flex: 1, marginBottom: 0 }}
                />
                <DatePicker
                  value={endDate}
                  onChange={setEndDate}
                  placeholder="End Date"
                  containerStyle={{ flex: 1, marginBottom: 0 }}
                />
                {(startDate || endDate) && (
                  <TouchableOpacity
                    style={[
                      styles.filterButton,
                      { borderColor: colors.danger },
                    ]}
                    onPress={() => {
                      setStartDate("");
                      setEndDate("");
                    }}
                  >
                    <Text style={{ color: colors.danger }}>Clear</Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
          )}

          {/* Area / Route Filter */}
          <SearchableSelect
            label="Area / Route"
            placeholder="All areas"
            options={areas.map((a) => ({
              label: a.name,
              value: a.id.toString(),
            }))}
            value={selectedAreaId}
            onValueChange={(val) => setSelectedAreaId(val)}
            loading={loadingAreas}
            containerStyle={styles.inputContainer}
            clearable
          />

          {/* Employee filter – visible to all users */}
          <SearchableSelect
            label="Created By (Employee)"
            placeholder="All employees"
            options={employees.map((emp) => ({
              label: `${emp.ownerName} ${emp.email ? `(${emp.email})` : ""}`,
              value: emp.id.toString(),
            }))}
            value={selectedEmployeeId}
            onValueChange={handleEmployeeSelect}
            onSearch={setEmployeeSearch}
            loading={employeesLoading}
            containerStyle={styles.inputContainer}
            clearable
          />

          {/* Customer filter – filtered by selected area */}
          <SearchableSelect
            label="Customer"
            placeholder="Select customer (optional)"
            options={getCustomerOptions()}
            value={selectedCustomerId}
            onValueChange={handleCustomerSelect}
            onSearch={setCustomerSearch}
            loading={customersLoading}
            containerStyle={styles.inputContainer}
            clearable
          />

          <View style={styles.buttonRow}>
            <Button
              title="Generate Report"
              onPress={fetchReport}
              loading={loading}
              variant="primary"
            />
            <Button
              title="Download PDF"
              onPress={handleDownloadPdf}
              loading={downloading}
              variant="outline"
            />
          </View>

          {reportDateRange && !useToday && (
            <Text style={{ color: colors.textLight, marginBottom: spacing.md }}>
              Period: {new Date(reportDateRange.start).toLocaleDateString()} -{" "}
              {new Date(reportDateRange.end).toLocaleDateString()}
            </Text>
          )}

          {employeeName && (
            <Text style={{ color: colors.textLight, marginBottom: spacing.xs }}>
              Employee: {employeeName}
            </Text>
          )}
          {customerNameText && (
            <Text style={{ color: colors.textLight, marginBottom: spacing.md }}>
              Customer: {customerNameText}
            </Text>
          )}

          <FlatList
            data={products}
            keyExtractor={(item) => item.productId.toString()}
            renderItem={renderProductItem}
            scrollEnabled={false}
            ListEmptyComponent={
              !loading && products.length === 0 ? (
                <Text style={styles.emptyText}>
                  No data. Click "Generate Report".
                </Text>
              ) : null
            }
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}
